/**
 * PersoneelPage — Employee management (admin only)
 */

import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { nl } from 'date-fns/locale';
import { Card, CardContent } from '../components/ui';
import { StaffTable } from '../components/personeel/StaffTable';
import { StaffForm } from '../components/personeel/StaffForm';
import { InviteStaffModal } from '../components/personeel/InviteStaffModal';
import { ContractBeslisKaarten } from '../components/personeel/ContractBeslisKaart';
import { ZiekteverzuimWidget } from '../components/personeel/ZiekteverzuimWidget';
import { useWorkforceStore } from '../stores';
import { supabase } from '../lib/supabase';
import type { Staff, GenerateEntitlementsResult } from '../stores/workforce-store';
import { formatHours } from '../lib/hours-calculator';

type View = 'list' | 'add' | 'edit';

interface SendResult {
  staff_id: string;
  name: string;
  status: 'sent' | 'skipped' | 'error';
  reason?: string;
}

export function PersoneelPage() {
  const {
    staffList,
    isLoadingStaff,
    fetchStaff,
    createStaff,
    updateStaff,
    deleteStaff,
    generateEntitlements,
    beslispunten,
    uitDienstTeVerwerken,
    fetchBeslispunten,
  } = useWorkforceStore();

  const navigate = useNavigate();
  const [view, setView] = useState<View>('list');
  const [selectedStaff, setSelectedStaff] = useState<Staff | null>(null);
  const [showInactive, setShowInactive] = useState(false);

  // Vakantiepotje-generatie state
  const [potjesPreview, setPotjesPreview] = useState<GenerateEntitlementsResult | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [potjesResult, setPotjesResult] = useState<string | null>(null);

  // PIN sending state
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [pinMode, setPinMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [isSending, setIsSending] = useState(false);
  const [sendResults, setSendResults] = useState<SendResult[] | null>(null);

  useEffect(() => {
    // Volledige lijst incl. inactief + uitgenodigd, anders filtert de server
    // ze al weg en heeft de "Toon inactief"-toggle niets om te tonen.
    fetchStaff(true);
    fetchBeslispunten();
  }, [fetchStaff, fetchBeslispunten]);

  // Uitgenodigde medewerkers (nog niet actief) zijn altijd zichtbaar — je wilt
  // net na het uitnodigen juist zien of het gelukt is. "Toon inactief" schakelt
  // daarbovenop de echt uit-dienst medewerkers in.
  const filteredStaff = staffList.filter(
    s => s.is_active || s.onboarding_status === 'invited' || showInactive
  );

  // Uit dienst gemeld (staff.end_date = uitdienstdatum) maar nog actief: de
  // API levert alleen wie geen open contract-beslispunt heeft en 30 dagen niet
  // klokte. Wie doorwerkt na een contracteinde komt als beslispunt binnen,
  // niet hier (Jorrit Hogenkamp, 03-11-2026).
  const handleDeactiveer = async (id: string, name: string) => {
    if (confirm(`${name} deactiveren? Team-app en rooster gaan dicht.`)) {
      await deleteStaff(id);
      await fetchBeslispunten();
    }
  };

  const handleSelect = (staff: Staff) => {
    navigate(`/personeel/${staff.id}`);
  };

  const handleSave = async (data: Partial<Staff>) => {
    if (view === 'edit' && selectedStaff) {
      await updateStaff(selectedStaff.id, data);
    } else {
      await createStaff(data);
    }
    setView('list');
    setSelectedStaff(null);
  };

  const handleDelete = async (id: string) => {
    if (confirm('Weet je zeker dat je deze medewerker wilt deactiveren?')) {
      await deleteStaff(id);
      setView('list');
      setSelectedStaff(null);
    }
  };

  const handleCancel = () => {
    setView('list');
    setSelectedStaff(null);
  };

  // PIN mode handlers
  const togglePinMode = () => {
    setPinMode(!pinMode);
    setSelectedIds(new Set());
    setSendResults(null);
  };

  const toggleSelect = useCallback((id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleAll = useCallback(() => {
    setSelectedIds(prev => {
      const allSelected = filteredStaff.every(s => prev.has(s.id));
      if (allSelected) return new Set();
      return new Set(filteredStaff.map(s => s.id));
    });
  }, [filteredStaff]);

  const selectReady = useCallback(() => {
    // Select all staff that have both email and pin_code
    const readyIds = filteredStaff
      .filter(s => s.email && s.pin_code)
      .map(s => s.id);
    setSelectedIds(new Set(readyIds));
  }, [filteredStaff]);

  const handleSendPins = async () => {
    if (selectedIds.size === 0) return;

    const count = selectedIds.size;
    if (!confirm(`PIN-email versturen naar ${count} medewerker${count === 1 ? '' : 's'}?`)) return;

    setIsSending(true);
    setSendResults(null);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      const response = await fetch('/api/workforce/send-pin', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session?.access_token}`,
        },
        body: JSON.stringify({ staff_ids: Array.from(selectedIds) }),
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Verzenden mislukt');
      setSendResults(data.results);
    } catch (err) {
      alert(`Fout: ${err instanceof Error ? err.message : 'Onbekende fout'}`);
    } finally {
      setIsSending(false);
    }
  };

  const readyCount = filteredStaff.filter(s => s.email && s.pin_code).length;

  // Vakantiepotjes: eerst dry-run (preview), daarna bevestigen
  const currentYear = new Date().getFullYear();
  const handlePotjesPreview = async () => {
    setIsGenerating(true);
    setPotjesResult(null);
    const result = await generateEntitlements(currentYear, true);
    setPotjesPreview(result);
    setIsGenerating(false);
    if (!result) setPotjesResult('Preview ophalen mislukt');
  };

  const handlePotjesConfirm = async () => {
    setIsGenerating(true);
    const result = await generateEntitlements(currentYear, false);
    setIsGenerating(false);
    setPotjesPreview(null);
    setPotjesResult(result?.success
      ? `${result.created} vakantiepotje${result.created === 1 ? '' : 's'} ${currentYear} aangemaakt`
      : 'Genereren mislukt');
  };

  return (
    <>
      <div className="p-6 max-w-7xl mx-auto overflow-y-auto h-full">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-heading font-semibold text-main">Personeel</h1>
            <p className="text-muted text-sm">{filteredStaff.length} medewerkers</p>
          </div>
          {view === 'list' && (
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2 text-sm text-muted cursor-pointer">
                <input
                  type="checkbox"
                  checked={showInactive}
                  onChange={e => setShowInactive(e.target.checked)}
                  className="rounded border-gray-300"
                />
                Toon inactief
              </label>
              <button
                onClick={togglePinMode}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  pinMode
                    ? 'bg-slate-200 text-slate-700 hover:bg-slate-300'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                {pinMode ? 'Annuleer' : 'Stuur PIN'}
              </button>
              {!pinMode && (
                <>
                  <button
                    onClick={handlePotjesPreview}
                    disabled={isGenerating}
                    className="px-4 py-2 bg-slate-100 text-slate-600 rounded-lg text-sm font-medium hover:bg-slate-200 disabled:opacity-50 transition-colors"
                  >
                    {isGenerating ? 'Berekenen...' : `Vakantiepotjes ${currentYear}`}
                  </button>
                  <button
                    onClick={() => setInviteModalOpen(true)}
                    className="px-4 py-2 bg-emerald-500 text-white rounded-lg text-sm font-medium hover:bg-emerald-600 transition-colors"
                  >
                    + Uitnodigen
                  </button>
                  <button
                    onClick={() => setView('add')}
                    className="px-4 py-2 bg-indigo-500 text-white rounded-lg text-sm font-medium hover:bg-indigo-600 transition-colors"
                  >
                    + Toevoegen
                  </button>
                </>
              )}
            </div>
          )}
        </div>

        {/* PIN mode action bar */}
        {pinMode && view === 'list' && (
          <div className="mb-4 p-4 bg-indigo-50 border border-indigo-200 rounded-xl flex items-center justify-between">
            <div className="flex items-center gap-4">
              <p className="text-sm text-indigo-800">
                <span className="font-semibold">{selectedIds.size}</span> geselecteerd
                {readyCount < filteredStaff.length && (
                  <span className="text-indigo-500 ml-2">
                    ({readyCount} van {filteredStaff.length} klaar voor verzending)
                  </span>
                )}
              </p>
              <button
                onClick={selectReady}
                className="text-sm text-indigo-600 hover:text-indigo-800 underline"
              >
                Selecteer alle klaar
              </button>
            </div>
            <button
              onClick={handleSendPins}
              disabled={selectedIds.size === 0 || isSending}
              className="px-4 py-2 bg-indigo-500 text-white rounded-lg text-sm font-medium hover:bg-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
            >
              {isSending && (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              )}
              {isSending ? 'Versturen...' : `Verstuur PIN (${selectedIds.size})`}
            </button>
          </div>
        )}

        {/* Send results */}
        {sendResults && (
          <div className="mb-4 p-4 bg-white border border-slate-200 rounded-xl">
            <h3 className="text-sm font-semibold text-main mb-3">Resultaat</h3>
            <div className="space-y-1.5">
              {sendResults.map(r => (
                <div key={r.staff_id} className="flex items-center gap-2 text-sm">
                  {r.status === 'sent' && (
                    <span className="w-5 h-5 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center text-xs">
                      &#10003;
                    </span>
                  )}
                  {r.status === 'skipped' && (
                    <span className="w-5 h-5 rounded-full bg-amber-100 text-amber-600 flex items-center justify-center text-xs">
                      &#8212;
                    </span>
                  )}
                  {r.status === 'error' && (
                    <span className="w-5 h-5 rounded-full bg-red-100 text-red-600 flex items-center justify-center text-xs">
                      &#10007;
                    </span>
                  )}
                  <span className="text-main">{r.name}</span>
                  {r.status === 'sent' && <span className="text-emerald-600">Verstuurd</span>}
                  {r.status === 'skipped' && <span className="text-amber-600">{r.reason}</span>}
                  {r.status === 'error' && <span className="text-red-600">{r.reason}</span>}
                </div>
              ))}
            </div>
            <button
              onClick={() => { setSendResults(null); setPinMode(false); setSelectedIds(new Set()); }}
              className="mt-3 text-sm text-indigo-600 hover:text-indigo-800"
            >
              Sluiten
            </button>
          </div>
        )}

        {/* Vakantiepotjes dry-run preview */}
        {potjesPreview && (
          <div className="mb-4 p-4 bg-white border border-slate-200 rounded-xl">
            <h3 className="text-sm font-semibold text-main mb-1">Vakantiepotjes {potjesPreview.year} — voorstel</h3>
            <p className="text-xs text-muted mb-3">
              Dagen × (uren per week / 5), pro-rata bij in- of uitdiensttreding dit jaar. Bestaande potjes worden overgeslagen.
            </p>
            <div className="space-y-1.5 mb-4">
              {potjesPreview.proposals.map(p => (
                <div key={p.staff_id} className="flex items-center justify-between text-sm">
                  <span className="text-main">{p.name}</span>
                  {p.skipped ? (
                    <span className="text-xs text-slate-400">{p.skipped}</span>
                  ) : (
                    <span className="font-medium text-main">
                      {formatHours(p.total_hours)}
                      <span className="text-xs text-muted font-normal ml-2">
                        ({p.dagen} dg × {p.uren_per_week}u/wk{p.pro_rata < 1 ? `, pro-rata ${Math.round(p.pro_rata * 100)}%` : ''})
                      </span>
                    </span>
                  )}
                </div>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={handlePotjesConfirm}
                disabled={isGenerating || potjesPreview.proposals.filter(p => !p.skipped).length === 0}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 disabled:opacity-50 transition-colors"
              >
                {isGenerating ? 'Aanmaken...' : `${potjesPreview.proposals.filter(p => !p.skipped).length} potjes aanmaken`}
              </button>
              <button
                onClick={() => setPotjesPreview(null)}
                className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg text-sm transition-colors"
              >
                Annuleren
              </button>
            </div>
          </div>
        )}

        {potjesResult && (
          <div className="mb-4 p-3 bg-emerald-50 border border-emerald-200 rounded-xl flex items-center justify-between">
            <p className="text-sm text-emerald-800">{potjesResult}</p>
            <button onClick={() => setPotjesResult(null)} className="text-xs text-emerald-600 hover:text-emerald-800">Sluiten</button>
          </div>
        )}

        {/* Contracteinde: beslispunten met knoppen (api/workforce/contract-einde) */}
        {view === 'list' && <ContractBeslisKaarten beslispunten={beslispunten} />}

        {/* Uit dienst gemeld, nog actief */}
        {view === 'list' && uitDienstTeVerwerken.length > 0 && (
          <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-xl">
            <p className="text-sm font-medium text-amber-800">
              {uitDienstTeVerwerken.length} {uitDienstTeVerwerken.length === 1 ? 'medewerker staat' : 'medewerkers staan'} uit dienst gemeld maar nog actief
            </p>
            <p className="text-xs text-amber-700 mt-0.5 mb-3">
              De uitdienstdatum is voorbij en er is 30 dagen niet geklokt. Deactiveren sluit team-app en rooster.
            </p>
            <div className="flex flex-wrap gap-2">
              {uitDienstTeVerwerken.map(s => (
                <span key={s.id} className="inline-flex items-center gap-2 px-3 py-1.5 bg-white border border-amber-300 rounded-lg text-sm text-amber-800">
                  <button onClick={() => navigate(`/personeel/${s.id}`)} className="hover:underline">
                    {s.name}
                    <span className="text-amber-500"> · uit dienst per {format(new Date(s.end_date + 'T00:00:00'), 'd MMM yyyy', { locale: nl })}</span>
                  </button>
                  <button onClick={() => handleDeactiveer(s.id, s.name)} className="text-xs font-medium text-red-600 hover:text-red-800">
                    Deactiveren
                  </button>
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Ziekteverzuim widget */}
        {view === 'list' && <ZiekteverzuimWidget staffList={staffList} />}

        {/* Content */}
        <Card variant="elevated" padding="none">
          <CardContent className="p-6">
            {view === 'list' ? (
              <StaffTable
                staff={filteredStaff}
                onSelect={handleSelect}
                isLoading={isLoadingStaff}
                selectable={pinMode}
                selectedIds={selectedIds}
                onToggleSelect={toggleSelect}
                onToggleAll={toggleAll}
              />
            ) : (
              <StaffForm
                staff={view === 'edit' ? selectedStaff : null}
                onSave={handleSave}
                onCancel={handleCancel}
                onDelete={view === 'edit' ? handleDelete : undefined}
              />
            )}
          </CardContent>
        </Card>
      </div>

      {inviteModalOpen && (
        <InviteStaffModal
          onClose={() => setInviteModalOpen(false)}
          onSuccess={() => { fetchStaff(); }}
        />
      )}
    </>
  );
}
